import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AjaxContext, AppContext, UserContext, ERROR_SEVERITY, EApiResponseCode } from 'commons-ui';
import * as lodash from 'lodash';
import { EBonusMoneyLambdaURL } from './helper';

// Define an interface for the type of items we're working with
interface BonusMoneyLambdaItem {
	id?: string;
	eventName: string;
	status: string;
	cashbackDenomInRs: number;
	bonusDenomInRs: number;
	totalUserCount: number;
	totalBonusAmountInRs: number;
	totalCashbackAmountInRs: number;
	bonusExpiryTime?: number;
	cashbackExpiryTime?: number;
	approvedBy?: string;
	approvedAt?: number;
	scheduledBy?: string;
	scheduledAt?: number;
	createdAt?: number;
	updatedAt?: number;
	cronExpression?: string;
	fileName?: string;
	s3Path?: string;
	[key: string]: any; // Allow for other properties
}

// Define page size as a constant at module level - set to 200 explicitly
const API_PAGE_SIZE = 100;

export const useService = () => {
	const { showAlert } = useContext(AppContext);
	const [data, setData] = useState<BonusMoneyLambdaItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName }
	} = useContext(AppContext);
	const { user } = useContext(UserContext);

	// Keep track of mounted state to prevent state updates after unmount
	const isMounted = useRef(true);
	// Track if initial data is loaded to prevent indefinite API calls
	const dataLoaded = useRef(false);

	useEffect(() => {
		// Set mounted flag
		isMounted.current = true;

		return () => {
			// Clear mounted flag on unmount
			isMounted.current = false;
			// Reset data loaded flag when component unmounts
			dataLoaded.current = false;
		};
	}, []);

	// Helper function to safely convert to number
	const safeNumber = (value: any): number => {
		if (value === null || value === undefined) return 0;
		const num = Number(value);
		return Number.isNaN(num) ? 0 : num;
	};

	const fetchData = useCallback(
		async (params?: any) => {
			// Return cached data if already loaded (unless explicitly requested to refresh)
			const forceRefresh = params?.forceRefresh;
			if (dataLoaded.current && !forceRefresh && !params) {
				return { data, rowCount: totalCount };
			}

			setLoading(true);
			const isTableRequest = !!params;

			try {
				// Build request body with explicit pageSize value
				const requestBody = {
					pageNo: 1, // Always get first page for client-side processing
					pageSize: API_PAGE_SIZE // Using explicit constant value of 200
				};

				// Make API request
				const response = await postData({
					endpoint: EBonusMoneyLambdaURL.LIST_LAMBDA_REQUEST_DATA,
					queryParams: {
						appName
					},
					body: requestBody
				});

				// Handle error responses
				if (response?.status !== EApiResponseCode.SUCCESS) {
					showAlert('Internal server error', { type: ERROR_SEVERITY.ERROR });
					setLoading(false);

					if (isTableRequest) {
						return { data: [], rowCount: 0 };
					}
					setData([]);
					return undefined;
				}

				if (response?.data?.success === false) {
					showAlert(`${lodash.get(response, ['data', 'externalMessage'], 'Error in api')}`, { type: ERROR_SEVERITY.ERROR });
					setLoading(false);

					if (isTableRequest) {
						return { data: [], rowCount: 0 };
					}
					setData([]);
					return undefined;
				}

				// Process the response data
				const rawItems = lodash.get(response, ['data', 'items'], []) || [];
				const count = lodash.get(response, ['data', 'totalCount'], 0);

				// Normalize and validate the items
				const items: BonusMoneyLambdaItem[] = Array.isArray(rawItems)
					? rawItems.map((rawItem, index) => {
							if (!rawItem) {
								// Create a default item if rawItem is empty or null
								return {
									id: `empty-${index}`,
									eventName: '',
									status: '',
									cashbackDenomInRs: 0,
									bonusDenomInRs: 0,
									totalUserCount: 0,
									totalBonusAmountInRs: 0,
									totalCashbackAmountInRs: 0
								};
							}

							// Create a well-structured item with defaults
							return {
								// Ensure the item has an ID
								id: rawItem.eventName || rawItem.id || `item-${index}`,

								// Required string fields
								eventName: rawItem.eventName || '',
								status: rawItem.status || '',

								// Required numeric fields with safe conversion
								cashbackDenomInRs: safeNumber(rawItem.cashbackDenomInRs),
								bonusDenomInRs: safeNumber(rawItem.bonusDenomInRs),
								totalUserCount: safeNumber(rawItem.totalUserCount),
								totalBonusAmountInRs: safeNumber(rawItem.totalBonusAmountInRs),
								totalCashbackAmountInRs: safeNumber(rawItem.totalCashbackAmountInRs),

								// Optional fields
								bonusExpiryTime: rawItem.bonusExpiryTime,
								cashbackExpiryTime: rawItem.cashbackExpiryTime,
								approvedBy: rawItem.approvedBy,
								approvedAt: rawItem.approvedAt,
								scheduledBy: rawItem.scheduledBy,
								scheduledAt: rawItem.scheduledAt,
								createdAt: rawItem.createdAt,
								updatedAt: rawItem.updatedAt,
								cronExpression: rawItem.cronExpression,
								fileName: rawItem.fileName,
								s3Path: rawItem.s3Path,

								// Include any other fields from the API
								...rawItem
							};
					  })
					: [];

				// Update the component state if not processing a table request
				if (!isTableRequest && isMounted.current) {
					setData(items);
					setTotalCount(count);
				}

				setLoading(false);

				// Mark data as loaded to prevent unnecessary future API calls
				dataLoaded.current = true;

				// Return formatted data for the table
				if (isTableRequest) {
					return {
						data: items,
						rowCount: count
					};
				}

				return { data: items, rowCount: count };
			} catch (error) {
				// Silent error handling
				setLoading(false);

				if (isTableRequest) {
					return {
						data: [],
						rowCount: 0
					};
				}

				if (isMounted.current) {
					setData([]);
				}

				return undefined;
			}
		},
		[data, totalCount, appName, postData, showAlert]
	);

	// Update lambda status function
	const updateLambdaStatus = useCallback(
		async (eventName: string, newStatus: string, scheduledAt?: number) => {
			try {
				const response = await postData({
					endpoint: EBonusMoneyLambdaURL.UPDATE_LAMBDA_REQUEST_STATUS,
					queryParams: {
						appName
					},
					body: {
						eventName,
						newStatus,
						...(scheduledAt ? { scheduledAt } : {}),
						emailId: user?.email
					}
				});

				if (response?.status !== EApiResponseCode.SUCCESS) {
					showAlert('Internal server error', { type: ERROR_SEVERITY.ERROR });
					return false;
				}

				if (response?.data?.success === false) {
					showAlert(`${lodash.get(response, ['data', 'externalMessage'], 'Error in api')}`, { type: ERROR_SEVERITY.ERROR });
					return false;
				}

				// Reset data loaded flag to force a refresh on next fetch
				dataLoaded.current = false;

				// Refresh data (with force refresh)
				fetchData({ forceRefresh: true });
				return true;
			} catch (error) {
				return false;
			}
		},
		[appName, user?.email, fetchData, postData, showAlert]
	);

	return {
		data,
		loading,
		totalCount,
		fetchData,
		updateLambdaStatus
	};
};
